import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import { Banner, Loading, Empty, formatDateTime, errorMessage } from '../components/ui';
import './AuditLog.css';

const TABS = [
  { key: 'activity', label: 'Activity' },
  { key: 'emails', label: 'Emails' },
  { key: 'logins', label: 'Sign-ins' },
];

function AuditLog() {
  const [tab, setTab] = useState('activity');

  const [rows, setRows] = useState([]);
  const [emails, setEmails] = useState([]);
  const [logins, setLogins] = useState([]);
  const [facets, setFacets] = useState({ entityTypes: [], actions: [], users: [] });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [entityType, setEntityType] = useState('');
  const [performedBy, setPerformedBy] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  let user = {};
  try { user = JSON.parse(localStorage.getItem('user') || '{}'); } catch { user = {}; }
  const isSuperAdmin = user.role === 'Super Admin';

  const loadActivity = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page, pageSize: 100 };
      if (search.trim()) params.search = search.trim();
      if (entityType) params.entityType = entityType;
      if (performedBy) params.performedBy = performedBy;

      const [res, facetRes] = await Promise.all([
        axios.get(`${API_BASE}/api/audit`, { params }),
        axios.get(`${API_BASE}/api/audit/facets`),
      ]);

      setRows(res.data.rows);
      setTotalPages(res.data.totalPages);
      setTotal(res.data.total);
      setFacets(facetRes.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the activity log.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, entityType, performedBy]);

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/audit/emails`);
      setEmails(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the email log.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLogins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/audit/logins`);
      setLogins(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the sign-in log.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'activity') loadActivity();
    else if (tab === 'emails') loadEmails();
    else if (tab === 'logins') loadLogins();
  }, [tab, loadActivity, loadEmails, loadLogins]);

  const exportXlsx = () => {
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (search.trim()) params.set('search', search.trim());
    if (entityType) params.set('entityType', entityType);
    if (performedBy) params.set('performedBy', performedBy);

    fetch(`${API_BASE}/api/audit/export?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Export failed.');
        const disposition = res.headers.get('content-disposition') || '';
        const match = disposition.match(/filename="?([^"]+)"?/i);
        return res.blob().then((blob) => ({ blob, name: match ? match[1] : 'Activity Log.xlsx' }));
      })
      .then(({ blob, name }) => {
        const href = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = href;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(href);
      })
      .catch((err) => setError(err.message));
  };

  const clearFilters = () => {
    setSearch('');
    setEntityType('');
    setPerformedBy('');
    setPage(1);
  };

  const hasFilters = !!(search || entityType || performedBy);

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>Activity Log</h2>
            <p className="page-subtitle">
              Every decision, edit, document generation and email attempt, with who did it and
              when. Read-only — nothing here can be edited or removed.
            </p>
          </div>
          {tab === 'activity' && (
            <div className="page-header-actions">
              <button className="btn btn-secondary" onClick={exportXlsx}>Export to Excel</button>
            </div>
          )}
        </div>

        <Banner kind="error" message={error} onDismiss={() => setError('')} />

        <div className="tab-bar">
          {TABS.filter((t) => t.key !== 'logins' || isSuperAdmin).map((t) => (
            <button
              key={t.key}
              className={`tab${tab === t.key ? ' is-active' : ''}`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'activity' && (
          <>
            <div className="filter-row">
              <input
                className="filter-search"
                type="text"
                placeholder="Search action, user or details…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                aria-label="Search the activity log"
              />
              <select value={entityType} onChange={(e) => { setEntityType(e.target.value); setPage(1); }} aria-label="Filter by area">
                <option value="">All areas</option>
                {facets.entityTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={performedBy} onChange={(e) => { setPerformedBy(e.target.value); setPage(1); }} aria-label="Filter by user">
                <option value="">All users</option>
                {facets.users.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              {hasFilters && (
                <button className="btn btn-ghost btn-sm filter-clear" onClick={clearFilters}>Clear filters</button>
              )}
            </div>

            {loading ? (
              <Loading />
            ) : rows.length === 0 ? (
              <Empty title="Nothing logged yet." hint={hasFilters ? 'Try clearing the filters.' : undefined} />
            ) : (
              <>
                <div className="table-wrap">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th className="nowrap">When</th>
                        <th className="nowrap">Area</th>
                        <th>Action</th>
                        <th>Change</th>
                        <th>Details</th>
                        <th className="nowrap">By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.Id}>
                          <td className="nowrap">{formatDateTime(r.PerformedAt)}</td>
                          <td className="nowrap">
                            <span className="badge badge-grey">
                              {r.EntityType}{r.EntityId ? ` #${r.EntityId}` : ''}
                            </span>
                          </td>
                          <td>{r.Action}</td>
                          <td className="audit-change">
                            {r.FieldName ? (
                              <>
                                <strong>{r.FieldName}</strong><br />
                                <span className="from">{r.OldValue || '(blank)'}</span>
                                {' → '}
                                <span className="to">{r.NewValue || '(blank)'}</span>
                              </>
                            ) : '—'}
                          </td>
                          <td className="audit-details">{r.Details || '—'}</td>
                          <td className="nowrap">{r.PerformedBy || 'system'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="audit-pager">
                  <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    Previous
                  </button>
                  <span>Page {page} of {totalPages} · {total} entries</span>
                  <button className="btn btn-secondary btn-sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                    Next
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {tab === 'emails' && (
          loading ? <Loading /> : emails.length === 0 ? (
            <Empty title="No emails sent yet." />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="nowrap">When</th>
                    <th>Applicant</th>
                    <th>To</th>
                    <th>Subject</th>
                    <th className="nowrap">Kind</th>
                    <th className="nowrap">Result</th>
                    <th className="nowrap">By</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((e) => (
                    <tr key={e.Id}>
                      <td className="nowrap">{formatDateTime(e.SentAt)}</td>
                      <td>{e.CompanyName || '—'}</td>
                      <td>{e.ToEmail}</td>
                      <td className="audit-details">{e.Subject}</td>
                      <td className="nowrap">
                        {e.IsTest ? <span className="badge badge-amber">Test</span> : <span className="badge badge-grey">Live</span>}
                      </td>
                      <td className="nowrap">
                        <span className={`badge ${e.Status === 'Sent' ? 'badge-green' : 'badge-red'}`}>{e.Status}</span>
                        {e.ErrorMessage && <div className="audit-details">{e.ErrorMessage}</div>}
                      </td>
                      <td className="nowrap">{e.SentBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === 'logins' && (
          loading ? <Loading /> : logins.length === 0 ? (
            <Empty title="No sign-ins recorded yet." />
          ) : (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="nowrap">When</th>
                    <th>Email</th>
                    <th className="nowrap">Result</th>
                    <th>Reason</th>
                    <th className="nowrap">IP address</th>
                  </tr>
                </thead>
                <tbody>
                  {logins.map((l) => (
                    <tr key={l.Id}>
                      <td className="nowrap">{formatDateTime(l.LoggedInAt)}</td>
                      <td>{l.Email}</td>
                      <td className="nowrap">
                        <span className={`badge ${l.Success ? 'badge-green' : 'badge-red'}`}>
                          {l.Success ? 'Success' : 'Failed'}
                        </span>
                      </td>
                      <td>{l.Reason || '—'}</td>
                      <td className="nowrap">{l.IPAddress || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default AuditLog;
